import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import {
  useDeleteProposalTemplateMutation,
  useGetProposalTemplatesQuery,
} from "work/proposal-templates/_api/proposal-template-api";
import useStartDraftFromProposalTemplate from "work/proposal-templates/_hooks/use-start-draft-from-proposal-template";
import { deleteTemplateNamed } from "work/proposal-templates/delete-proposal-template";
import ProposalTemplate from "work/proposal-templates/proposal-template";

export const CREATE_FROM_TEMPLATE_LABEL = "Create from Template";
export const CREATE_FROM_TEMPLATE_DIALOG_TITLE = "Create from a Proposal Template";
export const PROPOSAL_TEMPLATE_PICKER_LABEL = "Proposal template";
export const CREATE_FROM_TEMPLATE_DESCRIPTION =
  "Start a new draft from a proposal template you saved. Nothing is saved until you save the draft.";
export const NO_PROPOSAL_TEMPLATES_TEXT =
  "You have no proposal templates yet. Open a proposal and choose Save as Proposal Template.";

/**
 * Picks the saved proposal template a new draft starts from (#6129), from the caller's own templates
 * for the side they are working on, newest first. A template can be deleted from here as well, since
 * this is the one place a person sees their templates.
 */
export default function CreateFromProposalTemplateDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const session = useSession();
  const draftFromTemplate = useStartDraftFromProposalTemplate();
  const [deleteTemplate, deletion] = useDeleteProposalTemplateMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selected, setSelected] = useState<ProposalTemplate | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const templatesQuery = useGetProposalTemplatesQuery(open ? { search: debouncedSearchTerm || undefined } : skipToken);
  const templates = useMemo<ProposalTemplate[]>(
    () => ProposalTemplate.fromJSONArray(templatesQuery.data).filter((template) => template.isUsableIn(session)),
    [templatesQuery.data, session],
  );

  function handleClose() {
    setSelected(null);
    setSearchTerm("");
    setConfirmingDelete(false);
    onClose();
  }

  async function handleStart() {
    if (!selected) return;
    const started = await draftFromTemplate.start(selected.id);
    if (started) handleClose();
  }

  async function handleDelete() {
    if (!selected) return;
    const deleted = await deleteTemplateNamed(selected, (templateId) => deleteTemplate({ templateId }).unwrap());
    setConfirmingDelete(false);
    if (deleted) {
      setSelected(null);
      setSearchTerm("");
    }
  }

  const busy = draftFromTemplate.isStarting || deletion.isLoading;
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="create-from-template-title">
      <DialogTitle id="create-from-template-title">{CREATE_FROM_TEMPLATE_DIALOG_TITLE}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{CREATE_FROM_TEMPLATE_DESCRIPTION}</DialogContentText>
        <Autocomplete
          options={templates}
          value={selected}
          loading={templatesQuery.isFetching}
          filterOptions={(options) => options}
          isOptionEqualToValue={(option, value) => option.id.isEqualTo(value.id)}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option.id.value}
          renderOption={(optionProps, option) => {
            const { key, ...rest } = optionProps;
            return (
              <li key={key} {...rest}>
                {option.name}
                {option.proposalName && option.proposalName !== option.name ? ` (${option.proposalName})` : ""}
              </li>
            );
          }}
          inputValue={searchTerm}
          onInputChange={(_event, value) => setSearchTerm(value)}
          onChange={(_event, value) => {
            setSelected(value);
            setConfirmingDelete(false);
          }}
          noOptionsText={
            templatesQuery.isError
              ? "Could not load proposal templates."
              : debouncedSearchTerm
                ? "No proposal templates found."
                : NO_PROPOSAL_TEMPLATES_TEXT
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={PROPOSAL_TEMPLATE_PICKER_LABEL}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {templatesQuery.isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        {confirmingDelete && selected ? (
          <DialogContentText sx={{ mt: 2 }} role="alert">
            Delete the proposal template {selected.name}? Drafts already started from it do not change.
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions>
        {confirmingDelete ? (
          <>
            <Button disabled={busy} onClick={() => setConfirmingDelete(false)}>
              Keep Template
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={busy}
              loading={deletion.isLoading}
              onClick={() => void handleDelete()}
            >
              Delete Template
            </Button>
          </>
        ) : (
          <>
            <Button
              color="error"
              disabled={!selected || busy}
              onClick={() => setConfirmingDelete(true)}
              sx={{ mr: "auto" }}
            >
              Delete
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!selected || busy}
              loading={draftFromTemplate.isStarting}
              onClick={() => void handleStart()}
            >
              Start Draft
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
